<script setup>
import { computed, useSlots, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  showMobileSidebar: {
    type: Boolean,
    default: true,
  },
  showMobileSidebarToggle: {
    type: Boolean,
    default: true,
  },
  mobilePresentationOnDesktop: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'goToContactsList',
  'openEditContact',
  'openNewPurchaseProject',
]);

const { t } = useI18n();
const slots = useSlots();
const route = useRoute();

const isContactSidebarOpen = ref(false);
const showActionsDropdown = ref(false);

const contactId = computed(() => route.params.contactId);

const contactActions = computed(() => [
  {
    label: t('EDIT_CONTACT.BUTTON_LABEL'),
    action: 'edit',
    value: 'edit',
    icon: 'i-lucide-square-pen',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE'),
    action: 'message',
    value: 'message',
    icon: 'i-lucide-message-square',
  },
  {
    label: t('PURCHASE_PROJECTS.FORM.TITLE'),
    action: 'new_purchase_project',
    value: 'new_purchase_project',
    icon: 'i-lucide-shopping-bag',
  },
]);

const rootClass = computed(() =>
  props.mobilePresentationOnDesktop
    ? 'bg-n-background'
    : 'bg-n-background lg:bg-n-surface-1'
);

const contentShellClass = computed(() =>
  props.mobilePresentationOnDesktop ? '' : 'ltr:2xl:ml-56 rtl:2xl:mr-56'
);

const headerClass = computed(() =>
  props.mobilePresentationOnDesktop
    ? 'bg-n-solid-1 lg:bg-n-background'
    : 'bg-n-solid-1 lg:bg-transparent 3xl:px-0'
);

const headerContentClass = computed(() =>
  props.mobilePresentationOnDesktop ? '' : 'lg:py-7'
);

const mainClass = computed(() =>
  props.mobilePresentationOnDesktop ? '' : 'lg:px-6 3xl:px-px'
);

const mainContentClass = computed(() =>
  props.mobilePresentationOnDesktop ? 'lg:pt-4' : 'lg:py-4'
);

const sidebarOverlayClass = computed(() =>
  props.mobilePresentationOnDesktop ? '' : 'lg:hidden'
);

const sidebarSheetClass = computed(() =>
  props.mobilePresentationOnDesktop ? 'md:max-w-[40.625rem]' : ''
);

const sidebarBackdropClass = computed(() => [
  sidebarOverlayClass.value,
  isContactSidebarOpen.value
    ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
    : 'bg-transparent',
]);

const goBack = () => {
  emit('goToContactsList');
};

const closeActionsDropdown = () => {
  if (!showActionsDropdown.value) return;
  showActionsDropdown.value = false;
};

const handleActionMenu = ({ action }, toggle) => {
  showActionsDropdown.value = false;

  if (action === 'edit') {
    emit('openEditContact');
    return;
  }

  if (action === 'message') {
    toggle();
    return;
  }

  if (action === 'new_purchase_project') {
    emit('openNewPurchaseProject');
  }
};

const handleConversationSidebarToggle = () => {
  isContactSidebarOpen.value = !isContactSidebarOpen.value;
};

const closeMobileSidebar = () => {
  if (!isContactSidebarOpen.value) return;
  isContactSidebarOpen.value = false;
};

const openMobileSidebar = () => {
  isContactSidebarOpen.value = true;
};

defineExpose({
  openMobileSidebar,
  closeMobileSidebar,
});
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden justify-evenly"
    :class="rootClass"
  >
    <div
      class="flex flex-col w-full h-full transition-all duration-300"
      :class="contentShellClass"
    >
      <header class="sticky top-0 z-10 px-4" :class="headerClass">
        <div class="w-full mx-auto max-w-[40.625rem]">
          <div
            class="flex items-center justify-between w-full pt-5 pb-2"
            :class="headerContentClass"
          >
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-chevron-left"
              class="!rounded-full bg-n-alpha-black2"
              :aria-label="t('GENERAL_SETTINGS.BACK')"
              @click="goBack"
            />
            <ComposeConversation :contact-id="contactId">
              <template #trigger="{ toggle }">
                <div
                  v-on-clickaway="closeActionsDropdown"
                  class="relative flex items-center justify-end gap-2"
                >
                  <slot name="header-actions" />
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    icon="i-lucide-ellipsis"
                    class="!rounded-full bg-n-alpha-black2"
                    :aria-label="t('CONTACT_PANEL.CONTACT_ACTIONS')"
                    :class="showActionsDropdown ? 'bg-n-alpha-2' : ''"
                    @click="showActionsDropdown = !showActionsDropdown"
                  />
                  <DropdownMenu
                    v-if="showActionsDropdown"
                    :menu-items="contactActions"
                    class="top-full mt-1 ltr:right-0 rtl:left-0 w-max"
                    @action="handleActionMenu($event, toggle)"
                  />
                </div>
              </template>
            </ComposeConversation>
          </div>
        </div>
      </header>
      <main class="flex-1 overflow-y-auto" :class="mainClass">
        <div
          class="w-full pt-0 pb-4 mx-auto max-w-[40.625rem]"
          :class="mainContentClass"
        >
          <slot name="default" />
        </div>
      </main>
    </div>

    <!-- Desktop sidebar -->
    <div
      v-if="slots.sidebar && !props.mobilePresentationOnDesktop"
      class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
    >
      <slot name="sidebar" />
    </div>

    <!-- Mobile sidebar container -->
    <div
      v-if="slots.sidebar && props.showMobileSidebar"
      class="fixed inset-0 z-[60] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="sidebarBackdropClass"
      @click.self="closeMobileSidebar"
    >
      <!-- Toggle button -->
      <div
        v-if="props.showMobileSidebarToggle"
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#contact-sidebar-content'] },
        ]"
        class="absolute flex items-start p-1 w-fit h-fit pointer-events-auto xs:top-24 top-28 ltr:right-6 rtl:left-6 transition-all bg-n-solid-2 border border-n-weak duration-500 ease-in-out"
        :class="[
          isContactSidebarOpen
            ? 'justify-end ltr:rounded-l-full rtl:rounded-r-full ltr:rounded-r-none rtl:rounded-l-none'
            : 'justify-center rounded-full ltr:mr-6 rtl:ml-6',
        ]"
      >
        <Button
          ghost
          slate
          sm
          class="!rounded-full rtl:rotate-180"
          :class="{ 'bg-n-alpha-2': isContactSidebarOpen }"
          :icon="
            isContactSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          data-contact-sidebar-toggle
          @click="handleConversationSidebarToggle"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="translate-y-full"
        enter-to-class="translate-y-0"
        leave-from-class="translate-y-0"
        leave-to-class="translate-y-full"
      >
        <div
          v-if="isContactSidebarOpen"
          id="contact-sidebar-content"
          class="w-full h-[92vh] max-h-[92vh] pointer-events-auto bg-n-solid-2 overflow-y-auto rounded-t-[1.5rem] shadow-xl"
          :class="sidebarSheetClass"
        >
          <div class="flex items-center justify-start px-4 pt-4 pb-2">
            <Button
              ghost
              slate
              sm
              icon="i-lucide-x"
              class="!rounded-full bg-n-alpha-black2"
              :aria-label="t('GENERAL.CLOSE')"
              @click="closeMobileSidebar"
            />
          </div>
          <slot name="sidebar" />
        </div>
      </Transition>
    </div>
  </section>
</template>
